import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Copy, Ban, Link2, ExternalLink, Check } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/shared/empty-state'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import {
  fetchPublicLinksData,
  createPublicLink,
  revokePublicLink,
  publicLinkUrl,
  type DestinationStatus,
  type PublicLink,
  type PublishedForm,
  type PublicLinkInput,
} from '../api/public-links-api'

const DESTINATION_LABELS: Record<DestinationStatus, string> = {
  in_progress: 'در حال تکمیل (بررسی توسط کارشناس)',
  submitted_for_review: 'در انتظار بازبینی ناظر',
  approved: 'تأیید خودکار',
}

/** Destinations valid for the project's review mode (mirrors server constraint). */
function allowedDestinations(reviewMode: 'required' | 'none'): DestinationStatus[] {
  return reviewMode === 'required'
    ? ['submitted_for_review', 'in_progress']
    : ['approved', 'in_progress']
}

interface CreateModalProps {
  open: boolean
  onClose: () => void
  forms: PublishedForm[]
  reviewMode: 'required' | 'none'
  onSave: (d: PublicLinkInput) => void
  saving: boolean
}

function CreateLinkModal({ open, onClose, forms, reviewMode, onSave, saving }: CreateModalProps) {
  const dests = allowedDestinations(reviewMode)
  const [title, setTitle] = useState('')
  const [formIds, setFormIds] = useState<string[]>([])
  const [destination, setDestination] = useState<DestinationStatus>(dests[0])
  const [collectContact, setCollectContact] = useState(false)
  const [allowFiles, setAllowFiles] = useState(false)
  const [expiresAt, setExpiresAt] = useState('')
  const [maxSubmissions, setMaxSubmissions] = useState('')
  const [perHour, setPerHour] = useState('5')
  const [perDay, setPerDay] = useState('50')

  useEffect(() => {
    if (open) {
      setTitle(''); setFormIds([]); setDestination(dests[0])
      setCollectContact(false); setAllowFiles(false)
      setExpiresAt(''); setMaxSubmissions(''); setPerHour('5'); setPerDay('50')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const toggleForm = (id: string) =>
    setFormIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  // allow_files only meaningful if a selected form actually has file fields.
  const selectedHaveFiles = forms.some((f) => formIds.includes(f.id) && f.has_file_fields)

  const submit = () =>
    onSave({
      title: title.trim(),
      form_ids: formIds,
      destination_status: destination,
      collect_contact: collectContact,
      allow_files: allowFiles && selectedHaveFiles,
      expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      max_submissions: maxSubmissions ? Math.max(1, parseInt(maxSubmissions, 10)) : null,
      rate_limit_per_hour: Math.max(0, parseInt(perHour, 10) || 0),
      rate_limit_per_day: Math.max(0, parseInt(perDay, 10) || 0),
    })

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>ساخت لینک عمومی</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label>عنوان لینک</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="مثال: نظرسنجی دانشجویان" />
          </div>

          <div className="space-y-2">
            <Label>فرم‌های قابل تکمیل</Label>
            {forms.length === 0 ? (
              <p className="text-xs text-muted-foreground">هیچ فرم منتشرشده‌ای در این پروژه وجود ندارد.</p>
            ) : (
              <div className="border border-border rounded-lg divide-y divide-border max-h-48 overflow-y-auto">
                {forms.map((f) => (
                  <label key={f.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-muted/50">
                    <Checkbox checked={formIds.includes(f.id)} onCheckedChange={() => toggleForm(f.id)} />
                    <span className="text-sm flex-1">{f.title}</span>
                    {f.has_file_fields && (
                      <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">دارای فایل</span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>وضعیت سابقه پس از تکمیل</Label>
            <div className="space-y-2">
              {dests.map((d) => (
                <label key={d} className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="radio"
                    name="destination"
                    checked={destination === d}
                    onChange={() => setDestination(d)}
                    className="mt-1"
                  />
                  <span className="text-sm">{DESTINATION_LABELS[d]}</span>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">
              گزینه‌ها بر اساس حالت بازبینی پروژه ({reviewMode === 'required' ? 'بازبینی الزامی' : 'بدون بازبینی'}) محدود شده‌اند.
            </p>
          </div>

          <div className="space-y-2.5">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Checkbox checked={collectContact} onCheckedChange={(v) => setCollectContact(Boolean(v))} />
              <span className="text-sm">دریافت نام و ایمیل اختیاری از پرکننده</span>
            </label>
            <label className={`flex items-center gap-2.5 ${selectedHaveFiles ? 'cursor-pointer' : 'opacity-50'}`}>
              <Checkbox
                checked={allowFiles && selectedHaveFiles}
                disabled={!selectedHaveFiles}
                onCheckedChange={(v) => setAllowFiles(Boolean(v))}
              />
              <span className="text-sm">اجازه بارگذاری فایل {!selectedHaveFiles && '(فرم انتخابی فیلد فایل ندارد)'}</span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>تاریخ انقضا (اختیاری)</Label>
              <Input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} dir="ltr" />
            </div>
            <div className="space-y-1.5">
              <Label>سقف تعداد ارسال (اختیاری)</Label>
              <Input type="number" min={1} value={maxSubmissions} onChange={(e) => setMaxSubmissions(e.target.value)} placeholder="نامحدود" dir="ltr" />
            </div>
            <div className="space-y-1.5">
              <Label>محدودیت در ساعت (هر IP)</Label>
              <Input type="number" min={0} value={perHour} onChange={(e) => setPerHour(e.target.value)} dir="ltr" />
            </div>
            <div className="space-y-1.5">
              <Label>محدودیت در روز (هر IP)</Label>
              <Input type="number" min={0} value={perDay} onChange={(e) => setPerDay(e.target.value)} dir="ltr" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>انصراف</Button>
          <Button onClick={submit} disabled={saving || !title.trim() || formIds.length === 0}>
            {saving ? 'در حال ساخت...' : 'ساخت لینک'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function LinkRow({ link, forms, onCopy, onRevoke, revoking }: {
  link: PublicLink
  forms: PublishedForm[]
  onCopy: (token: string) => void
  onRevoke: (id: string) => void
  revoking: boolean
}) {
  const [copied, setCopied] = useState(false)
  const expired = link.expires_at ? new Date(link.expires_at) < new Date() : false
  const capped = link.max_submissions != null && link.submission_count >= link.max_submissions
  const active = !link.is_revoked && !expired && !capped
  const formTitles = link.form_ids
    .map((id) => forms.find((f) => f.id === id)?.title)
    .filter(Boolean)

  const copy = () => {
    onCopy(link.token)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className="px-4 py-3.5">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground truncate">{link.title}</p>
            {active ? (
              <span className="text-[10px] bg-emerald-500/15 text-emerald-500 px-1.5 py-0.5 rounded">فعال</span>
            ) : (
              <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                {link.is_revoked ? 'لغو شده' : expired ? 'منقضی' : 'تکمیل ظرفیت'}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {formTitles.length} فرم · {DESTINATION_LABELS[link.destination_status]}
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            ارسال‌ها: {link.submission_count}{link.max_submissions != null ? ` / ${link.max_submissions}` : ''}
            {link.expires_at && ` · انقضا: ${new Date(link.expires_at).toLocaleDateString('fa-IR')}`}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" onClick={copy} disabled={!active} title="کپی لینک">
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'کپی شد' : 'کپی'}
          </Button>
          <Button
            size="sm" variant="ghost" className="h-7 w-7 p-0"
            onClick={() => window.open(publicLinkUrl(link.token), '_blank')}
            disabled={!active}
            title="باز کردن"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
          {!link.is_revoked && (
            <Button
              size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"
              onClick={() => onRevoke(link.id)}
              disabled={revoking}
              title="لغو لینک"
            >
              <Ban className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export function PublicLinksContent() {
  const { projectId } = useParams<{ projectId: string }>()
  const qc = useQueryClient()
  const { toast } = useToast()
  const user = useAuthStore((s) => s.user)
  const [createOpen, setCreateOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['public-links', projectId],
    queryFn: () => fetchPublicLinksData(projectId!),
    enabled: !!projectId,
  })

  const createMutation = useMutation({
    mutationFn: (input: PublicLinkInput) => createPublicLink(projectId!, user!.id, input),
    onSuccess: (link) => {
      qc.invalidateQueries({ queryKey: ['public-links', projectId] })
      setCreateOpen(false)
      navigator.clipboard?.writeText(publicLinkUrl(link.token)).catch(() => {})
      toast({ title: 'لینک ساخته شد', description: 'نشانی لینک در کلیپ‌بورد کپی شد.' })
    },
    onError: (err) => toast({ title: 'خطا در ساخت لینک', description: (err as Error).message, variant: 'destructive' }),
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokePublicLink(id, user!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['public-links', projectId] })
      toast({ title: 'لینک لغو شد' })
    },
    onError: (err) => toast({ title: 'خطا', description: (err as Error).message, variant: 'destructive' }),
  })

  const copy = (token: string) => {
    navigator.clipboard?.writeText(publicLinkUrl(token)).catch(() => {})
    toast({ title: 'کپی شد', description: 'نشانی لینک در کلیپ‌بورد قرار گرفت.' })
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
    )
  }

  if (!data?.enabled) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 text-center">
        <Link2 className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm font-medium text-foreground mb-1">لینک‌های عمومی غیرفعال است</p>
        <p className="text-xs text-muted-foreground">
          برای فعال‌سازی، از تب «دسترسی‌ها» گزینهٔ «لینک‌های عمومی فرم» را روشن کنید.
        </p>
      </div>
    )
  }

  const links = data.links
  const forms = data.forms

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          لینک‌های اشتراک‌گذاری برای تکمیل فرم بدون نیاز به ورود.
        </p>
        <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1.5 shrink-0" disabled={forms.length === 0}>
          <Plus className="w-4 h-4" />
          ساخت لینک
        </Button>
      </div>

      {links.length === 0 ? (
        <EmptyState
          icon={Link2}
          title="لینکی ساخته نشده"
          description="یک لینک عمومی برای جمع‌آوری داده بدون ورود کاربر بسازید"
          action={
            <Button size="sm" onClick={() => setCreateOpen(true)} disabled={forms.length === 0}>
              <Plus className="w-4 h-4 me-1.5" />
              ساخت لینک
            </Button>
          }
        />
      ) : (
        <div className="bg-card border border-border rounded-xl divide-y divide-border">
          {links.map((link) => (
            <LinkRow
              key={link.id}
              link={link}
              forms={forms}
              onCopy={copy}
              onRevoke={(id) => revokeMutation.mutate(id)}
              revoking={revokeMutation.isPending}
            />
          ))}
        </div>
      )}

      <CreateLinkModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        forms={forms}
        reviewMode={data.reviewMode}
        onSave={(d) => createMutation.mutate(d)}
        saving={createMutation.isPending}
      />
    </>
  )
}

export function PublicLinksPage() {
  return (
    <div className="flex flex-col h-full">
      <PageHeader title="لینک‌های عمومی" breadcrumbs={[{ label: 'لینک‌های عمومی' }]} />
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl">
          <PublicLinksContent />
        </div>
      </div>
    </div>
  )
}
